import { useEffect, useRef } from 'react';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

/**
 * Tells Node a challenge registrant left without booking a call with Rob, so
 * their AI welcome call comes in about a minute instead of after the 10-minute
 * fallback (Rob, 2026-10-22). Node only moves an already queued call earlier;
 * it never queues one and never undoes a booking.
 *
 * Authorization is the signed booking reference from the registration
 * response, sent as a Bearer token. `keepalive` lets the request finish while
 * the page unloads. Sent at most once per reference per page load.
 */
const sentReferences = new Set();

export function sendLeftWithoutBooking(
  readToken,
  apiUrl = getApiBaseUrl() || 'https://api.botspot.trade',
) {
  if (!readToken || sentReferences.has(readToken)) return false;
  sentReferences.add(readToken);
  try {
    fetch(`${apiUrl}/courses/challenge-registrations/left-booking-page`, {
      method: 'POST',
      keepalive: true,
      headers: { Authorization: `Bearer ${readToken}` },
    }).catch(() => {
      /* Best effort: the 10-minute fallback still calls them. */
    });
  } catch {
    /* Best effort: the 10-minute fallback still calls them. */
  }
  return true;
}

/**
 * Sends the signal when the page is hidden for good (pagehide: tab closed,
 * another site, a reload) and, with `onUnmount`, when the visitor navigates
 * elsewhere inside the app. `bookedRef.current === true` (a Calendly booking)
 * always suppresses it.
 */
export function useLeftWithoutBooking(readToken, { bookedRef, onUnmount = false, apiUrl } = {}) {
  const latest = useRef({ readToken, bookedRef, apiUrl });
  latest.current = { readToken, bookedRef, apiUrl };
  const mounted = useRef(false);

  useEffect(() => {
    if (!readToken) return undefined;
    mounted.current = true;
    const leave = () => {
      const { readToken: token, bookedRef: booked, apiUrl: base } = latest.current;
      if (booked?.current) return;
      sendLeftWithoutBooking(token, base || undefined);
    };
    window.addEventListener('pagehide', leave);
    return () => {
      mounted.current = false;
      window.removeEventListener('pagehide', leave);
      if (!onUnmount) return;
      // React StrictMode unmounts and remounts once in development. Only a
      // component that stays unmounted has really been navigated away from.
      window.setTimeout(() => {
        if (!mounted.current) leave();
      }, 0);
    };
  }, [readToken, onUnmount]);
}

/** Tests only. */
export function resetLeftWithoutBookingForTests() {
  sentReferences.clear();
}
